'use client';
import { useProductLayout } from '@/lib/providers/product-layout-provider';
import { MSProductWithFullDetailWithVaraintPreset } from '@/util/query/product-query';
import { CUSTOMER_LEVEL } from '@prisma/client';
import { TFunction } from 'i18next';
import { memo } from 'react';
import ProductCardBorderGradientShortDesc from '../products/cards/product-card-border-gradient-short-desc';
import ProductCardButtonBesidePrice from '../products/cards/product-card-button-beside-price';
import ProductCardButtonBesidePriceList from '../products/cards/product-card-button-beside-price-list';
import ProductCardDefault from '../products/cards/product-card-default';
import ProductCardGradient from '../products/cards/product-card-gradient';
import ProductCardSolid from '../products/cards/product-card-solid';
import ProductCardSolidInProductHighlight from '../products/cards/product-card-solid-in-product-highlight';
interface CollectionListItemProps {
    product: MSProductWithFullDetailWithVaraintPreset;
    href: string;
    t?: TFunction<'translation', undefined>;
    level: CUSTOMER_LEVEL;
    refreshfav?: boolean;
    variantId?: string;
    priorityImage?: boolean;
    shopCurrency: string;
    cardType?: ProductCardConfig; // allow caller override
    mobileColumn?: number;
    layoutMode?: 'vertical' | 'horizontal'; // layout mode for card
}

export enum ProductCardConfig {
    DEFAULT = 'DEFAULT',
    BUTTON_BESIDE_PRICE = 'BUTTON_BESIDE_PRICE',
    GRADIENT = 'GRADIENT',
    SOLID = 'SOLID',
    SOLID_IN_PRODUCT_HIGHLIGHT = 'SOLID_IN_PRODUCT_HIGHLIGHT',
    BORDER_GRADIENT_SHORT_DESC = 'BORDER_GRADIENT_SHORT_DESC',
}

const CollectionListItemInner = ({
    product,
    href,
    level,
    refreshfav,
    variantId,
    priorityImage,
    shopCurrency,
    cardType,
    mobileColumn,
    layoutMode,
}: CollectionListItemProps) => {
    const resolved = cardType ?? ProductCardConfig.DEFAULT;
    const productLayout = useProductLayout();
    const resolvedMobileColumn = mobileColumn ?? productLayout.mobileColumn;

    switch (resolved) {
        case ProductCardConfig.SOLID:
            return (
                <ProductCardSolid
                    product={product}
                    href={href}
                    level={level}
                    refreshfav={refreshfav}
                    variantId={variantId}
                    priorityImage={priorityImage}
                    shopCurrency={shopCurrency}
                    favoriteEnable={productLayout.favoriteEnable}
                />
            );
        case ProductCardConfig.SOLID_IN_PRODUCT_HIGHLIGHT:
            return (
                <ProductCardSolidInProductHighlight
                    product={product}
                    href={href}
                    level={level}
                    refreshfav={refreshfav}
                    variantId={variantId}
                    priorityImage={priorityImage}
                    shopCurrency={shopCurrency}
                    favoriteEnable={productLayout.favoriteEnable}
                    mobileColumn={resolvedMobileColumn}
                />
            );
        case ProductCardConfig.GRADIENT:
            return (
                <ProductCardGradient
                    product={product}
                    href={href}
                    level={level}
                    refreshfav={refreshfav}
                    variantId={variantId}
                    priorityImage={priorityImage}
                    shopCurrency={shopCurrency}
                    favoriteEnable={productLayout.favoriteEnable}
                    mobileColumn={resolvedMobileColumn}
                />
            );
        case ProductCardConfig.BUTTON_BESIDE_PRICE:
            if (layoutMode === 'vertical') {
                return (
                    <ProductCardButtonBesidePriceList
                        product={product}
                        href={href}
                        level={level}
                        refreshfav={refreshfav}
                        variantId={variantId}
                        priorityImage={priorityImage}
                        shopCurrency={shopCurrency}
                        favoriteEnable={productLayout.favoriteEnable}
                    />
                );
            }
            return (
                <ProductCardButtonBesidePrice
                    product={product}
                    href={href}
                    level={level}
                    refreshfav={refreshfav}
                    variantId={variantId}
                    priorityImage={priorityImage}
                    shopCurrency={shopCurrency}
                    favoriteEnable={productLayout.favoriteEnable}
                />
            );
        case ProductCardConfig.BORDER_GRADIENT_SHORT_DESC:
            return (
                <ProductCardBorderGradientShortDesc
                    product={product}
                    href={href}
                    level={level}
                    refreshfav={refreshfav}
                    variantId={variantId}
                    priorityImage={priorityImage}
                    shopCurrency={shopCurrency}
                    favoriteEnable={productLayout.favoriteEnable}
                />
            );
        case ProductCardConfig.DEFAULT:
        default:
            return (
                <ProductCardDefault
                    product={product}
                    href={href}
                    level={level}
                    refreshfav={refreshfav}
                    variantId={variantId}
                    priorityImage={priorityImage}
                    shopCurrency={shopCurrency}
                    favoriteEnable={productLayout.favoriteEnable}
                />
            );
    }
};

const CollectionListItem = memo(CollectionListItemInner, (a, b) => {
    if (a === b) return true;
    if (a.cardType !== b.cardType) return false;
    if (a.layoutMode !== b.layoutMode) return false;
    if (a.mobileColumn !== b.mobileColumn) return false;
    if (a.href !== b.href) return false;
    if (a.level !== b.level) return false;
    if (a.variantId !== b.variantId) return false;
    if (a.priorityImage !== b.priorityImage) return false;
    if (a.shopCurrency !== b.shopCurrency) return false;
    if (a.refreshfav !== b.refreshfav) return false;
    const ap = a.product;
    const bp = b.product;
    if (ap === bp) return true;
    if (!ap || !bp) return false;
    if (ap.id !== bp.id) return false;
    if (ap.attributes?.displayName !== bp.attributes?.displayName) return false;
    const av0 = ap.productVariant?.[0];
    const bv0 = bp.productVariant?.[0];
    if (!!av0 !== !!bv0) return false;
    if (av0 && bv0) {
        if (av0.id !== bv0.id) return false;
        if (av0.priceFull !== bv0.priceFull) return false;
        if (av0.comparePriceFull !== bv0.comparePriceFull) return false;
    }
    return true;
});

export default CollectionListItem;
